import type {
  AbbrevCandidateRow,
  AbbreviationsByShortResponse,
} from "@/api/terminology";

export interface CandidateOnlyHint {
  /** Сколько неразобранных кандидатов лежит в очереди. */
  candidateCount: number;
  /** Самое частое определение среди кандидатов (по числу клинреков-источников). */
  topDefinition: string | null;
}

function refsCount(c: AbbrevCandidateRow): number {
  const refs = c.source_refs;
  if (Array.isArray(refs) && refs.length) return refs.length;
  return typeof c.source_count === "number" ? c.source_count : 0;
}

/**
 * «Одобренных записей нет, но кандидаты есть» — единственная ситуация, когда
 * пустая выдача поиска не значит «сокращение неизвестно». Резолвер читает
 * ТОЛЬКО approved-записи, кандидаты для перевода инертны; пользователю надо
 * это сказать прямо и подсказать, что делать (завести запись), а не «попробуй
 * иначе». Возвращает null, если подсказка неуместна (есть approved / нет
 * кандидатов / ответ не пришёл).
 */
export function candidateOnlyHint(
  data: AbbreviationsByShortResponse | undefined,
): CandidateOnlyHint | null {
  if (!data) return null;
  if (data.summary.approved_count > 0 || data.abbreviations.length > 0) return null;
  const candidates = data.candidates ?? [];
  if (candidates.length === 0) return null;

  // Группируем по нормализованному определению, побеждает то, у которого
  // больше источников; при равенстве — первое встреченное (порядок бэкенда).
  const byDef = new Map<string, { text: string; refs: number }>();
  for (const c of candidates) {
    const text = (c.definition ?? "").trim();
    if (!text) continue;
    const key = text.toLowerCase();
    const cur = byDef.get(key);
    const refs = refsCount(c);
    if (cur) cur.refs += refs;
    else byDef.set(key, { text, refs });
  }
  let top: { text: string; refs: number } | null = null;
  for (const v of byDef.values()) {
    if (!top || v.refs > top.refs) top = v;
  }
  return {
    candidateCount: candidates.length,
    topDefinition: top ? top.text : null,
  };
}
